import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"

import { api } from "@/api/cliente"
import { Modal } from "@/componentes/ui/Modal"

import { Pie } from "./EditorEnte"
import { type Ente, type Obligacion, PERIODICIDADES, type Periodicidad } from "./tipos"

type Cargo = { id: number; nombre: string; activo: boolean }

/** Crear o editar una obligación. Se desactiva, no se borra: los registros la citan. */
export function EditorObligacion({
  obligacion,
  entes,
  enteInicial,
  onCerrar,
}: {
  obligacion: Obligacion | null
  entes: Ente[]
  enteInicial?: number
  onCerrar: () => void
}) {
  const clienteQuery = useQueryClient()
  const [d, setD] = useState({
    ente_id: obligacion?.ente_id ?? enteInicial ?? entes[0]?.id ?? 0,
    codigo: obligacion?.codigo ?? "",
    nombre: obligacion?.nombre ?? "",
    descripcion: obligacion?.descripcion ?? "",
    periodicidad: obligacion?.periodicidad ?? ("MENSUAL" as Periodicidad),
    dia_limite: obligacion?.dia_limite ? String(obligacion.dia_limite) : "",
    base_legal: obligacion?.base_legal ?? "",
    responsable_cargo_id: obligacion?.responsable_cargo_id
      ? String(obligacion.responsable_cargo_id)
      : "",
    activa: obligacion?.activa ?? true,
  })
  const cargos = useQuery({
    queryKey: ["cargos", "activos"],
    queryFn: () => api.get<Cargo[]>("/api/v1/cargos?solo_activos=true"),
  })
  const guardar = useMutation({
    mutationFn: () => {
      const cuerpo = {
        ...d,
        descripcion: d.descripcion.trim() || null,
        base_legal: d.base_legal.trim() || null,
        dia_limite: d.dia_limite ? Number(d.dia_limite) : null,
        responsable_cargo_id: d.responsable_cargo_id ? Number(d.responsable_cargo_id) : null,
      }
      return obligacion
        ? api.put<Obligacion>(`/api/v1/cumplimiento/obligaciones/${obligacion.id}`, cuerpo)
        : api.post<Obligacion>("/api/v1/cumplimiento/obligaciones", cuerpo)
    },
    onSuccess: () => {
      clienteQuery.invalidateQueries({ queryKey: ["cumplimiento"] })
      onCerrar()
    },
    meta: { exito: "Obligación guardada" },
  })
  const periodica = !["POR_VIGENCIA", "UNICA"].includes(d.periodicidad)

  return (
    <Modal
      abierto={true}
      titulo={obligacion ? `Editar ${obligacion.codigo}` : "Nueva obligación"}
      ancho="lg"
      onCerrar={onCerrar}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="ob-ente" className="mb-1 block text-xs font-medium uppercase">
            Ente
          </label>
          <select
            id="ob-ente"
            className="campo"
            value={d.ente_id}
            onChange={(e) => setD({ ...d, ente_id: Number(e.target.value) })}
          >
            {entes.map((e) => (
              <option key={e.id} value={e.id}>
                {e.codigo} · {e.nombre}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="ob-codigo" className="mb-1 block text-xs font-medium uppercase">
            Código
          </label>
          <input
            id="ob-codigo"
            className="campo font-mono"
            value={d.codigo}
            onChange={(e) => setD({ ...d, codigo: e.target.value.toUpperCase() })}
          />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="ob-nombre" className="mb-1 block text-xs font-medium uppercase">
            Nombre
          </label>
          <input
            id="ob-nombre"
            className="campo"
            value={d.nombre}
            onChange={(e) => setD({ ...d, nombre: e.target.value })}
          />
        </div>
        <div>
          <label htmlFor="ob-per" className="mb-1 block text-xs font-medium uppercase">
            Periodicidad
          </label>
          <select
            id="ob-per"
            className="campo"
            value={d.periodicidad}
            onChange={(e) => setD({ ...d, periodicidad: e.target.value as Periodicidad })}
          >
            {PERIODICIDADES.map((p) => (
              <option key={p.valor} value={p.valor}>
                {p.etiqueta}
              </option>
            ))}
          </select>
        </div>
        {periodica && (
          <div>
            <label htmlFor="ob-dia" className="mb-1 block text-xs font-medium uppercase">
              Día límite del mes
            </label>
            <input
              id="ob-dia"
              className="campo tabular-nums"
              inputMode="numeric"
              value={d.dia_limite}
              onChange={(e) => setD({ ...d, dia_limite: e.target.value.replace(/\D/g, "") })}
            />
          </div>
        )}
        <div>
          <label htmlFor="ob-base" className="mb-1 block text-xs font-medium uppercase">
            Base legal
          </label>
          <input
            id="ob-base"
            className="campo"
            value={d.base_legal}
            onChange={(e) => setD({ ...d, base_legal: e.target.value })}
          />
        </div>
        <div>
          <label htmlFor="ob-resp" className="mb-1 block text-xs font-medium uppercase">
            Cargo responsable
          </label>
          <select
            id="ob-resp"
            className="campo"
            value={d.responsable_cargo_id}
            disabled={cargos.isError}
            onChange={(e) => setD({ ...d, responsable_cargo_id: e.target.value })}
          >
            <option value="">Sin asignar</option>
            {(cargos.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.nombre}
              </option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="ob-desc" className="mb-1 block text-xs font-medium uppercase">
            Nota
          </label>
          <textarea
            id="ob-desc"
            rows={2}
            className="campo"
            value={d.descripcion}
            onChange={(e) => setD({ ...d, descripcion: e.target.value })}
          />
        </div>
        {obligacion && (
          <label className="flex items-center gap-1.5 text-sm sm:col-span-2">
            <input
              type="checkbox"
              checked={d.activa}
              onChange={(e) => setD({ ...d, activa: e.target.checked })}
            />
            Activa (sale en el tablero y avisa)
          </label>
        )}
      </div>
      <Pie
        error={guardar.error}
        pendiente={guardar.isPending}
        listo={d.ente_id > 0 && d.codigo.trim().length >= 2 && d.nombre.trim().length >= 2}
        onCerrar={onCerrar}
        onGuardar={() => guardar.mutate()}
      />
    </Modal>
  )
}
